import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Brain, Settings } from "lucide-react";
import { Wordmark } from "@/components/brand/Logo";
import { useBrainStore } from "@/stores/brainStore";
import { useNavItems, type NavItem } from "./useNavItems";
import { cn } from "@/lib/utils";

export function Sidebar() {
  const { t } = useTranslation();
  const items = useNavItems();

  return (
    <aside className="hidden lg:flex w-64 flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
      <div className="flex h-16 items-center px-6 border-b border-sidebar-border">
        <Wordmark className="text-sidebar-foreground" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5 scrollbar-thin">
        {items.map((item) => (
          <NavItemLink key={item.to} {...item} />
        ))}

        <div className="pt-4 mt-4 border-t border-sidebar-border space-y-0.5">
          <BrainTrigger />
        </div>
      </nav>
      <div className="px-3 py-3 border-t border-sidebar-border">
        <NavItemLink
          to="/app/settings/organization"
          label={t("nav.settings")}
          icon={Settings}
        />
      </div>
    </aside>
  );
}

function NavItemLink({ to, label, icon: Icon }: NavItem) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          isActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-sidebar-foreground/80",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function BrainTrigger() {
  const { t } = useTranslation();
  const open = useBrainStore((s) => s.open);

  return (
    <button
      type="button"
      onClick={() => open()}
      className={cn(
        "w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "text-sidebar-foreground/80",
        "min-h-[44px]",
      )}
    >
      <Brain className="h-4 w-4 shrink-0" />
      <span className="truncate">{t("nav.brain")}</span>
    </button>
  );
}